/**
 * "Needs My Approval" for tasks.
 *
 * A task is waiting on a seat when the next step in its signature chain is an
 * approve step. Who may take that step is a matter of seat name, so the match
 * is by case-insensitive substring against the roster position names the user
 * holds, which tolerates "Division/Group Supervisor" and similar variants.
 */

import {
  nextTaskApprovalCta,
  parseTaskSignatures,
  type TaskApprovalCtaKind,
} from '../actionTicket/taskSignatureSlots';

export type TaskApprovalKind = Extract<
  TaskApprovalCtaKind,
  'approve-execute-gs' | 'approve-execute-bd' | 'approve-completion'
>;

/** Seat-name substrings (lower case) that can take each approve step. */
export const TASK_APPROVAL_SEAT_SUBSTRINGS: Record<
  TaskApprovalKind,
  readonly string[]
> = {
  'approve-execute-gs': ['group supervisor'],
  'approve-execute-bd': ['branch director'],
  'approve-completion': [
    'operations section chief',
    'ops section chief',
    'planning section chief',
  ],
};

export function isTaskApprovalKind(
  kind: TaskApprovalCtaKind | null | undefined
): kind is TaskApprovalKind {
  return (
    kind === 'approve-execute-gs' ||
    kind === 'approve-execute-bd' ||
    kind === 'approve-completion'
  );
}

export function positionCanApprove(
  positionName: string,
  kind: TaskApprovalKind
): boolean {
  const name = positionName.trim().toLowerCase();
  if (!name) return false;
  return TASK_APPROVAL_SEAT_SUBSTRINGS[kind].some((needle) =>
    name.includes(needle)
  );
}

type ApprovalAction = {
  signatures?: unknown;
  status?: string | null;
};

/**
 * The approve step this task is waiting on, or null when it is not an approve
 * step.
 */
export function pendingTaskApprovalKind(
  action: ApprovalAction
): TaskApprovalKind | null {
  const cta = nextTaskApprovalCta(
    parseTaskSignatures(action.signatures),
    action.status
  );
  return isTaskApprovalKind(cta?.kind) ? cta.kind : null;
}

/** True when the task awaits an approval one of these seats can give. */
export function needsMyApproval(
  action: ApprovalAction,
  positionNames: readonly string[]
): boolean {
  const kind = pendingTaskApprovalKind(action);
  if (!kind) return false;
  return positionNames.some((name) => positionCanApprove(name, kind));
}
